import { ReactNode, CSSProperties, ElementType } from 'react';
import cn from 'classnames';
import './Text.style.scss';

/**
 * Text Variants by title
 */
export type TextVariant =
  | 'title-xxl'
  | 'title-xl'
  | 'title-l'
  | 'title-m'
  | 'title-s'
  | 'title-xs'
  | 'title-xxs'
  | 'title-xxs-500'
  | 'title-xxs-500-upper'
  | 'title-xxs-600'
  | 'title-xxs-600-upper'
  | 'title-xxs-700'
  | 'title-xxs-700-upper'
  | 'text-l'
  | 'text-m'
  | 'text-s'
  | 'text-s-500'
  | 'span-l'
  | 'span-m'
  | 'span-s'
  | 'span-s-500'
  | 'link-l'
  | 'link-xs';

type TextTypes = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7';

type TextProps = {
  /** className to target this specific component via CSS */
  className?: string;

  /**
   * Select one of many text variants available for use.
   * For HTML Tag identification, please use the following chart:

      | Name                | Tag    | Design System Token              |
      |---------------------|--------|----------------------------------|
      | title-xxl           | `<h1> `      | $vds.font.tn.title.xxl.200       |
      | title-xl            | `<h2> `      | $vds.font.tn.title.xl.200        |
      | title-l             | `<h3> `      | $vds.font.tn.title.l.300         |
      | title-m             | `<h4> `      | $vds.font.tn.title.m.300         |
      | title-s             | `<h5> `      | $vds.font.rh.title.s.500         |
      | title-xs            | `<h6> `      | $vds.font.rh.title.xs.600        |
      | title-xxs           | `<p>`        | $vds.font.rh.title.xxs.700       |
      | title-xxs-500       | `<p>`        | $vds.font.rh.title.xxs.500       |
      | title-xxs-500-upper | `<p>`        | $vds.font.rh.title.xxs.500.caps  |
      | title-xxs-600       | `<p>`        | $vds.font.rh.title.xxs.600       |
      | title-xxs-600-upper | `<p>`        | $vds.font.rh.title.xxs.600.caps  |
      | title-xxs-700       | `<p>`        | $vds.font.rh.title.xs.700        |
      | title-xxs-700-upper | `<p>`        | $vds.font.rh.title.xs.700.caps   |
      | text-l              | `<p>`        | $vds.font.rh.text.s.400          |
      | text-m              | `<p>`        | $vds.font.rh.text.xs.400         |
      | text-s              | `<p>`        | $vds.font.rh.text.xxs.400        |
      | span-l              | `<span>`     | $vds.font.rh.text.s.400          |
      | span-m              | `<span>`     | $vds.font.rh.text.xs.400         |
      | span-s              | `<span>`     | $vds.font.rh.text.xxs.400        |
      @default 'text-m'
   */
  variant?: TextVariant;

  /**
   * Override the rendered HTML element while keeping the variant's visual styles.
   * Use when semantic hierarchy (e.g. h1/h2/h3) must differ from the visual design token.
   */
  as?: TextTypes;

  /** Children/Text to render */
  children?: ReactNode;

  /** Styles all children text with italics */
  italic?: boolean;

  underline?: boolean;

  /** Transforms all children text to UPPERCASE */
  uppercase?: boolean;

  style?: CSSProperties;

  onClick?: () => void;
  'data-testid'?: string;
};

const defaultElementForVariant: Record<TextVariant, TextTypes> = {
  'title-xxl': 'h1',
  'title-xl': 'h2',
  'title-l': 'h3',
  'title-m': 'h4',
  'title-s': 'h5',
  'title-xs': 'h6',
  'title-xxs': 'p',
  'title-xxs-500': 'p',
  'title-xxs-500-upper': 'p',
  'title-xxs-600': 'p',
  'title-xxs-600-upper': 'p',
  'title-xxs-700': 'p',
  'title-xxs-700-upper': 'p',
  'text-l': 'p',
  'text-m': 'p',
  'text-s': 'p',
  'text-s-500': 'p',
  'span-l': 'span',
  'span-m': 'span',
  'span-s': 'span',
  'span-s-500': 'span',
  'link-l': 'p',
  'link-xs': 'p'
};

export default function Text(props: TextProps) {
  const {
    as,
    className = '',
    variant = 'text-m',
    children,
    italic,
    uppercase,
    underline,
    style = {},
    onClick,
    'data-testid': dataTestId
  } = props;

  const Tag = (as ?? defaultElementForVariant[variant]) as ElementType;

  return (
    <Tag
      style={style}
      className={cn(
        'Text',
        variant,
        { italic, uppercase, underline },
        className
      )}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {children}
    </Tag>
  );
}
